import React, { useEffect, useState } from "react";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertFromRaw, convertToRaw, ContentState } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { getVisitTransactions } from "../../../../apis/visit";

interface DischargeFormProps {
  initialData?: any;
  onSave: (data: any) => void;
  onCancel: () => void;
  visitId: number;
  provisionalDiagnosis?: any;
}

const DischargeForm: React.FC<DischargeFormProps> = ({
  initialData,
  onSave,
  onCancel,
  visitId,
  provisionalDiagnosis,
}) => {
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [billingInfo, setBillingInfo] = useState<{
    status?: string;
    totalAmount?: number;
    paidAmount?: number;
  }>({});
  const [formData, setFormData] = useState({
    vitals: {
      pulse: initialData?.vitals?.pulse || "",
      bp: initialData?.vitals?.bp || "",
      temp: initialData?.vitals?.temp || "",
      spo2: initialData?.vitals?.spo2 || "",
      rr: initialData?.vitals?.rr || "",
      cvs: initialData?.vitals?.cvs || "",
    },
    conditionAtDischarge: initialData?.conditionAtDischarge || "",
    followUpDate: initialData?.followUpDate || "",
    findings: initialData?.findings || "",
    notes: initialData?.notes || "",
  });

  useEffect(() => {
    const raw = initialData?.finalDiagnosis;
    if (raw) {
      try {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        const contentState = convertFromRaw(parsed);
        setEditorState(EditorState.createWithContent(contentState));
      } catch {
        setEditorState(
          EditorState.createWithContent(ContentState.createFromText(String(raw)))
        );
      }
    } else {
      setEditorState(EditorState.createEmpty());
    }
  }, [initialData?.finalDiagnosis]);

  useEffect(() => {
    const fetchBilling = async () => {
      try {
        const response: any = await getVisitTransactions(visitId);
        const invoice = response?.data?.invoice;
        setBillingInfo({
          status: invoice?.status,
          totalAmount: invoice?.totalAmount,
          paidAmount: invoice?.paidAmount,
        });
      } catch {
        setBillingInfo({});
      }
    };
    fetchBilling();
  }, [visitId]);

  const updateVital = (key: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      vitals: {
        ...prev.vitals,
        [key]: value,
      },
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canDischarge) return;
    const contentState = editorState.getCurrentContent();
    const finalDiagnosis = JSON.stringify(convertToRaw(contentState));
    onSave({
      ...formData,
      finalDiagnosis,
      dischargeDate: new Date().toISOString(),
    });
  };

  const billingAmount = billingInfo.totalAmount ?? 0;
  const billingPaid = billingInfo.paidAmount ?? 0;
  const billingStatus = billingInfo.status || "not_generated";
  const isBillGenerated = billingStatus !== "not_generated";
  const isBillPaid = billingPaid >= billingAmount;
  const canDischarge = isBillGenerated && isBillPaid;

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-5">
      <div className="border rounded-lg p-4 bg-gray-50">
        <div className="text-sm text-gray-600">Billing</div>
        <div className="mt-2 grid grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-gray-500">Amount</div>
            <div className="text-sm font-semibold text-gray-900">₹{billingAmount}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Status</div>
            <div className="text-sm font-semibold text-gray-900 capitalize">
              {billingStatus.replace(/_/g, " ")}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Paid</div>
            <div className="text-sm font-semibold text-gray-900">
              ₹{billingPaid}
            </div>
          </div>
        </div>
        {!canDischarge && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            Discharge is blocked. Please ensure the bill is generated and fully paid before discharging the patient.
          </div>
        )}
      </div>

      <div className="border rounded-lg p-4">
        <div className="text-sm font-medium text-gray-700 mb-1">
          Provisional Diagnosis
        </div>
        <div className="text-sm text-gray-900">
          {Array.isArray(provisionalDiagnosis)
            ? provisionalDiagnosis.map((d: any) => d.label || d.name || d).join(", ")
            : provisionalDiagnosis || "-"}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Final Diagnosis
        </label>
        <Editor
          editorState={editorState}
          onEditorStateChange={setEditorState}
          toolbarClassName="border border-gray-300 rounded-t-md"
          wrapperClassName="border border-gray-300 rounded-md"
          editorClassName="p-3 min-h-[200px] border-t border-gray-300"
          toolbar={{
            options: ["inline", "list", "blockType"],
            inline: {
              options: ["bold", "italic", "underline"],
            },
          }}
        />
      </div>

      <div>
        <div className="text-sm font-medium text-gray-700 mb-2">Vitals at Discharge</div>
        <div className="grid grid-cols-3 gap-3">
          <input
            type="text"
            placeholder="Pulse"
            value={formData.vitals.pulse}
            onChange={(e) => updateVital("pulse", e.target.value)}
            className="rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
          <input
            type="text"
            placeholder="BP"
            value={formData.vitals.bp}
            onChange={(e) => updateVital("bp", e.target.value)}
            className="rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
          <input
            type="text"
            placeholder="Temp"
            value={formData.vitals.temp}
            onChange={(e) => updateVital("temp", e.target.value)}
            className="rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
          <input
            type="text"
            placeholder="SpO2"
            value={formData.vitals.spo2}
            onChange={(e) => updateVital("spo2", e.target.value)}
            className="rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
          <input
            type="text"
            placeholder="RR"
            value={formData.vitals.rr}
            onChange={(e) => updateVital("rr", e.target.value)}
            className="rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
          <input
            type="text"
            placeholder="CVS"
            value={formData.vitals.cvs}
            onChange={(e) => updateVital("cvs", e.target.value)}
            className="rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Condition at Discharge
          </label>
          <input
            type="text"
            value={formData.conditionAtDischarge}
            onChange={(e) =>
              setFormData({ ...formData, conditionAtDischarge: e.target.value })
            }
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Follow Up Date
          </label>
          <input
            type="date"
            value={formData.followUpDate}
            onChange={(e) =>
              setFormData({ ...formData, followUpDate: e.target.value })
            }
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Findings</label>
          <textarea
            value={formData.findings}
            onChange={(e) => setFormData({ ...formData, findings: e.target.value })}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canDischarge}
          className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
            canDischarge ? "bg-indigo-600 hover:bg-indigo-700" : "bg-gray-400 cursor-not-allowed"
          }`}
        >
           Discharge Patient
        </button>
      </div>
    </form>
  );
};

export default DischargeForm;
